// src/services/roleService.js
import { getAllUsers } from './userService'; // To check role assignment before delete
import { ALL_PERMISSIONS, getDefaultPermissionSet, getAdminPermissionSet } from '../config/permissions'; // Import permission structure

const ROLES_STORAGE_KEY = 'loan_servicing_roles';

// --- Helper Functions ---
const getRolesFromStorage = () => {
    let parsedData = []; // Default to empty array
    try {
      const stored = localStorage.getItem(ROLES_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          parsedData = parsed;
        } else {
          console.warn("Roles data in localStorage was not an array. Resetting.");
          localStorage.removeItem(ROLES_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error("Error parsing Roles from localStorage:", error);
      localStorage.removeItem(ROLES_STORAGE_KEY);
    }
    return parsedData;
  };
  
  const saveRolesToStorage = (roles) => {
    if (!Array.isArray(roles)) {
      console.error("Save Error: Attempted to save non-array data as Roles.");
      throw new Error("Internal error: Invalid data type for Roles.");
    }
    try {
      localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(roles));
      console.log("Roles saved successfully.");
    } catch (error) {
      console.error("!!! Critical Error saving Roles to localStorage:", error);
      throw new Error(`Failed to save Roles to local storage: ${error.message}`);
    }
  };

// --- Initialize Default Roles ---
/* const initializeDefaultRoles = () => {
    let roles = getRolesFromStorage();
    if (roles.length === 0) { // Only initialize if completely empty
        console.log("Initializing default roles with permissions...");
        roles = [
            {
                id: 'admin', // Keep predictable ID for default admin
                name: 'Administrator',
                description: 'Full system access.',
                isSystemRole: true, // Flag to prevent deletion/major edits?
                permissions: getAdminPermissionSet() // All true
            },
            {
                id: 'manager',
                name: 'Manager',
                description: 'Manages loans, users (limited), reports.',
                isSystemRole: true,
                permissions: { // Example subset
                    ...getDefaultPermissionSet(), // Start with all false
                    dashboard: { view: true },
                    loans: { view: true, create: true, edit: true, delete: false, payoff: true, manageNonAccrual: true, manageEscrow: true },
                    borrowers: { view: true, create: true, edit: true, delete: false },
                    payments: { view: true, create: true, edit: false, delete: false },
                    fees: { view: true, create: true, edit: true, delete: false },
                    investors: { view: true, create: true, edit: true, delete: false },
                    remittances: { view: true, create: true },
                    reports: { view: true },
                    accounting: { viewAudit: true }, // Maybe view audit log
                    users: { view: true, edit: true }, // Can edit non-admins?
                    // ... set other relevant permissions to true ...
                }
            },
             {
                id: 'accountant',
                name: 'Accountant',
                description: 'Manages payments, accounting functions.',
                 isSystemRole: true,
                 permissions: {
                    ...getDefaultPermissionSet(),
                    dashboard: { view: true },
                    loans: { view: true }, // View only loans
                    payments: { view: true, create: true, edit: true, delete: false }, // Manage payments
                    fees: { view: true, create: true, edit: true, delete: false }, // Manage fees
                    investors: { view: true }, // View investors
                    remittances: { view: true, create: true }, // Manage remittances
                    accounting: { viewCOA: true, viewJE: true, generateJE: true, postJE: true, viewRecon: true, viewAudit: true }, // Full accounting access
                    // ... other relevant permissions ...
                 }
            },
             {
                id: 'staff',
                name: 'Staff',
                description: 'Basic loan viewing, payment recording, communication logs.',
                 isSystemRole: true,
                 permissions: {
                    ...getDefaultPermissionSet(),
                    dashboard: { view: true },
                    loans: { view: true }, // View only
                    borrowers: { view: true }, // View only
                    payments: { view: true, create: true }, // View & Record payments
                    fees: { view: true }, // View fees
                    communications: { view: true, create: true, edit: true, delete: true } // If comms module exists
                 }
            },
            // Add other default roles if needed
        ];
        saveRolesToStorage(roles);
    }
};
initializeDefaultRoles(); // Initialize roles on load
 */
const DEFAULT_ROLES_STRUCTURE = [
    { id: 'admin', name: 'Administrator', description: 'Full system access.', isSystemRole: true, permissions: getAdminPermissionSet() },
    { id: 'manager', name: 'Manager', description: 'Manages loans, users (limited), reports.', isSystemRole: true, permissions: { /* Example subset */ ...getDefaultPermissionSet(), dashboard:{view:true}, loans:{view:true, create:true, edit:true}, /* ... more */ } },
    { id: 'accountant', name: 'Accountant', description: 'Manages payments, accounting functions.', isSystemRole: true, permissions: { /* Example subset */ ...getDefaultPermissionSet(), dashboard:{view:true}, payments:{view:true, create:true}, accounting:{viewCOA: true, viewJE: true, generateJE: true, postJE: true, viewRecon: true, viewAudit: true}, /* ... more */ } },
    { id: 'staff', name: 'Staff', description: 'Basic loan viewing, payment recording, communication logs.', isSystemRole: true, permissions: { /* Example subset */ ...getDefaultPermissionSet(), dashboard:{view:true}, loans:{view:true}, payments:{view:true, create:true}, /* ... more */ } },
];

// --- Initialize Default Roles Function (Exported) ---
export const initializeDefaultRoles = () => {
    let roles = getRolesFromStorage(); // Use robust getter
    if (roles.length === 0) {
        console.log("Initializing default roles with permissions...");
        try {
            // Use the predefined structure
            saveRolesToStorage(DEFAULT_ROLES_STRUCTURE);
        } catch (error) {
            console.error("Failed to save initial default roles:", error);
        }
    } else {
        console.log("Roles data already exists.");
        // Optional: Add logic here to MERGE new permissions from DEFAULT_ROLES_STRUCTURE
        // into existing roles in local storage if the schema changes later.
    }
};
// --- CRUD Functions ---
export const getAllRoles = () => {
    // No need for async/await if just reading from storage
    const roles = getRolesFromStorage(); // Use robust helper
    console.log("getAllRoles returning:", roles);
    // Check if roles is an array before sorting
    if (!Array.isArray(roles)) {
        console.error("getAllRoles: Data from storage is not an array!", roles);
        return []; // Return empty array if data is invalid
    }
    // Sort by name
    return roles.sort((a,b) => (a.name || '').localeCompare(b.name || ''));
  };
  
  // ** Made synchronous **
  export const getRoleById = (id) => {
    // No need for async/await
    const roles = getRolesFromStorage();
    if (!Array.isArray(roles)) { return null; } // Added check
    return roles.find(role => role.id === id) || null;
  };

// Function needed by UI to build checkboxes
export const getPermissionSchema = () => {
    return ALL_PERMISSIONS;
};

export const createRole = async (roleData) => {
  // Expects: { name: string, description: string, permissions: object }
  await new Promise(resolve => setTimeout(resolve, 100));
  const roles = getRolesFromStorage();

  // Validation
  if (!roleData.name) throw new Error("Role name is required.");
  if (roles.some(r => r.name.toLowerCase() === roleData.name.toLowerCase())) {
      throw new Error(`Role name '${roleData.name}' already exists.`);
  }
  if (!roleData.permissions || typeof roleData.permissions !== 'object') {
       throw new Error("Permissions data is missing or invalid.");
  }

  const newRole = {
    id: `role_${Date.now()}`,
    isSystemRole: false, // User-created roles are not system roles
    ...roleData, // name, description, permissions
  };
  const updatedRoles = [...roles, newRole];
  saveRolesToStorage(updatedRoles);
  return newRole;
};

export const updateRole = async (id, updatedData) => {
   // Expects: { name: string, description: string, permissions: object }
   // Cannot update isSystemRole flag easily
  await new Promise(resolve => setTimeout(resolve, 100));
  let roles = getRolesFromStorage();
  const index = roles.findIndex(r => r.id === id);

  if (index > -1) {
    const originalRole = roles[index];
     // Validation
    if (!updatedData.name) throw new Error("Role name is required.");
    if (roles.some(r => r.id !== id && r.name.toLowerCase() === updatedData.name.toLowerCase())) {
         throw new Error(`Role name '${updatedData.name}' already exists.`);
    }
    if (!updatedData.permissions || typeof updatedData.permissions !== 'object') {
        throw new Error("Permissions data is missing or invalid.");
    }
    // Prevent changing system roles easily?
    // if (originalRole.isSystemRole && originalRole.name !== updatedData.name) {
    //      throw new Error("Cannot rename system roles.");
    // }

    roles[index] = {
        ...originalRole,
        name: updatedData.name,
        description: updatedData.description,
        permissions: updatedData.permissions, // Replace permissions object
    };
    saveRolesToStorage(roles);
    return roles[index];
  }
  return null; // Not found
};

export const deleteRole = async (id) => {
  await new Promise(resolve => setTimeout(resolve, 150)); // Longer for checks
  let roles = getRolesFromStorage();
  const roleToDelete = roles.find(r => r.id === id);

  if (!roleToDelete) return false; // Not found
  if (roleToDelete.isSystemRole) { // Prevent deleting default roles
      throw new Error(`Cannot delete system role '${roleToDelete.name}'.`);
  }

  // Check if any user has this role assigned
  const users = await getAllUsers(); // Fetch users (careful with circular deps if userService imports roleService)
  const isAssigned = users.some(user => user.role === id);
  if (isAssigned) {
      throw new Error(`Cannot delete role '${roleToDelete.name}' as it is currently assigned to one or more users.`);
  }

  const updatedRoles = roles.filter(r => r.id !== id);
  saveRolesToStorage(updatedRoles);
  return true;
};

// No need to export initializeDefaultRoles, it runs automatically.